import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, AlertCircle, Building2, Clock, Inbox, LayoutGrid, Mail, TrendingUp, Users } from 'lucide-react';
import { crmStatsApi } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import PageHeader from '../../components/PageHeader';
import { SOURCE_LABEL, formatHours, formatMoney, formatPercent, relativeTime } from '../../lib/crmInbox';
import type { CrmStats } from '../../types';

const PERIODS = [7, 30, 90];

const CONTACT_STATUS_COLOR: Record<string, string> = {
  lead: 'var(--scpa-text-muted)',
  prospect: 'var(--scpa-primary)',
  customer: 'var(--scpa-status-ok)',
  churned: 'var(--scpa-status-error)',
  archived: 'var(--scpa-text-muted)',
};

/** A headline number. `help` is the hover explanation; `tone` colors only
 *  the value, and only when it is a warning. */
function Tile({ label, value, sub, help, tone, to }: { label: string; value: string | number; sub?: string; help: string; tone?: 'err' | 'warn'; to?: string }) {
  const inner = (
    <div className="card space-y-1 h-full" title={help}>
      <span className="text-sm text-muted">{label}</span>
      <p className={`text-2xl font-bold ${tone === 'err' ? 'text-err' : tone === 'warn' ? 'text-warn' : 'text-fg'}`}>{value}</p>
      {sub && <p className="text-xs text-muted">{sub}</p>}
    </div>
  );
  return to ? <Link to={to} className="block hover:opacity-90">{inner}</Link> : inner;
}

/** Horizontal bars, direct-labeled: the name and count are text, the bar
 *  only shows proportion. One hue unless the rows carry their own color
 *  (pipeline stages, which the user picked). */
function Bars({ rows, valueLabel }: { rows: { label: string; count: number; color?: string; extra?: string }[]; valueLabel?: (n: number) => string }) {
  const max = Math.max(1, ...rows.map(r => r.count));
  if (rows.length === 0) return <p className="text-sm text-muted py-4 text-center">Nothing in this period.</p>;
  return (
    <div className="space-y-2">
      {rows.map(r => (
        <div key={r.label} className="text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="text-fg truncate flex items-center gap-1.5">
              {r.color && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />}
              {r.label}
            </span>
            <span className="text-muted whitespace-nowrap">{valueLabel ? valueLabel(r.count) : r.count}{r.extra ? ` · ${r.extra}` : ''}</span>
          </div>
          <div className="h-1.5 rounded-full bg-row-odd mt-1 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, backgroundColor: r.color ?? 'var(--scpa-primary)' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** New conversations per day, one series, hover for the exact count. */
function DayStrip({ byDay }: { byDay: { date: string; count: number }[] }) {
  const max = Math.max(1, ...byDay.map(d => d.count));
  const total = byDay.reduce((s, d) => s + d.count, 0);
  if (total === 0) return <p className="text-sm text-muted py-4 text-center">No new conversations in this period.</p>;
  return (
    <div>
      <div className="flex items-end gap-[2px] h-24" role="img" aria-label={`New conversations per day, ${total} total`}>
        {byDay.map(d => (
          <div key={d.date} className="flex-1 flex items-end h-full" title={`${new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}: ${d.count}`}>
            <div className="w-full rounded-t-[3px]" style={{ height: `${Math.max(d.count > 0 ? 6 : 2, (d.count / max) * 100)}%`, backgroundColor: d.count > 0 ? 'var(--scpa-primary)' : 'var(--scpa-border)' }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[11px] text-muted mt-1">
        <span>{new Date(byDay[0].date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
        <span>{total} new · peak {max}/day</span>
        <span>{new Date(byDay[byDay.length - 1].date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
      </div>
    </div>
  );
}

export default function CrmDashboard() {
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const [days, setDays] = useState(30);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['crm-stats', cid, days],
    queryFn: () => crmStatsApi.get(days),
    enabled: !!cid,
  });

  if (isLoading || !stats) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="h-8 bg-surface rounded w-48 animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="h-24 bg-surface rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }
  const s: CrmStats = stats;
  const period = `last ${s.period.days} days`;

  return (
    <div className="flex flex-col gap-6 pb-10">
      <PageHeader
        title="CRM Overview"
        intro="Who is waiting on you, how fast you answer, where leads come from and how deals are going. Hover any number for what it means."
        actions={
          <>
            <div className="flex rounded-lg border border-line overflow-hidden" role="group" aria-label="Period">
              {PERIODS.map(p => (
                <button key={p} type="button" onClick={() => setDays(p)} className={`px-3 py-1.5 text-xs font-semibold ${days === p ? 'bg-primary text-white' : 'bg-surface text-muted hover:text-fg'}`}>{p}d</button>
              ))}
            </div>
            <Link to="/crm/inbox" className="btn btn-primary inline-flex items-center gap-1.5"><Inbox className="w-4 h-4" /> Open Inbox</Link>
          </>
        }
      />

      {/* Inbox health */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile label="Needs a reply" value={s.inbox.needsReply} sub={s.inbox.overdue > 0 ? `${s.inbox.overdue} waiting over ${s.inbox.overdueAfterHours}h` : 'nothing overdue'} tone={s.inbox.overdue > 0 ? 'err' : undefined} help="Conversations where they wrote last and nobody has answered." to="/crm/inbox" />
        <Tile label="Unread" value={s.inbox.unread} sub={`${s.inbox.open + s.inbox.waiting} active in total`} help="New messages nobody has opened yet." to="/crm/inbox" />
        <Tile label="First response" value={formatHours(s.inbox.avgFirstResponseHours)} sub={s.inbox.medianFirstResponseHours != null ? `median ${formatHours(s.inbox.medianFirstResponseHours)} · ${s.inbox.repliedInPeriod} answered` : `no replies in the ${period}`} help={`Average time from a new inbound conversation to your first reply, ${period}.`} />
        <Tile label="New conversations" value={s.inbox.newInPeriod} sub={period} help={`Email, contact-form and outreach threads started in the ${period}.`} />
      </div>

      {/* Sales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile label="New leads" value={s.leads.newContacts} sub={`${s.leads.total} contacts in total`} help={`Contacts created in the ${period}, from any source.`} to="/crm/contacts" />
        <Tile label="Open deals" value={s.deals.open} sub={`${formatMoney(s.deals.openValue)} in pipeline${s.deals.closingSoon ? ` · ${s.deals.closingSoon} closing within 2 weeks` : ''}`} help="Deals not yet won or lost, and what they are worth together." to="/crm/deals/board" />
        <Tile label="Won" value={s.deals.wonInPeriod} sub={`${formatMoney(s.deals.wonValueInPeriod)} · ${period}`} help={`Deals marked won in the ${period}, and their value.`} />
        <Tile label="Win rate" value={formatPercent(s.deals.winRate)} sub={s.deals.winRate == null ? `no deals decided in the ${period}` : `${s.deals.lostInPeriod} lost${s.deals.avgCycleDays != null ? ` · ${Math.round(s.deals.avgCycleDays)}d to close` : ''}`} help={`Won ÷ (won + lost) for deals decided in the ${period}.`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attention list */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Waiting longest</h2>
            <Link to="/crm/inbox" className="text-xs text-primary hover:underline">Open inbox</Link>
          </div>
          {s.attention.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center">Nobody is waiting on a reply.</p>
          ) : (
            <ul className="divide-y divide-line">
              {s.attention.map(a => (
                <li key={a.id}>
                  <Link to={`/crm/inbox/${a.id}`} className="flex items-center gap-3 py-2 hover:bg-row-odd -mx-2 px-2 rounded">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-fg truncate"><span className="font-mono text-[11px] text-muted mr-1.5">CRM-{a.number}</span>{a.subject}</p>
                      <p className="text-xs text-muted truncate">{a.fromName || a.fromEmail} · {SOURCE_LABEL[a.source as keyof typeof SOURCE_LABEL] ?? a.source}{a.owner ? ` · ${a.owner.name}` : ' · unassigned'}</p>
                    </div>
                    <span className={`text-xs font-semibold whitespace-nowrap ${a.waitingHours >= 24 ? 'text-err' : a.waitingHours >= 4 ? 'text-warn' : 'text-muted'}`} title={a.lastInboundAt ? `Last message ${relativeTime(a.lastInboundAt)}` : undefined}>{formatHours(a.waitingHours)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Trend */}
        <div className="card">
          <h2 className="section-title flex items-center gap-2 mb-3"><Mail className="w-4 h-4" /> New conversations per day</h2>
          <DayStrip byDay={s.inbox.byDay} />
        </div>

        {/* Sources */}
        <div className="card">
          <h2 className="section-title flex items-center gap-2 mb-3"><Users className="w-4 h-4" /> Where leads come from</h2>
          <p className="text-xs text-muted mb-3">New contacts in the {period}, by how they first reached you.</p>
          <Bars rows={s.leads.bySource.map(r => ({ label: r.source, count: r.count }))} />
          {s.inbox.bySource.length > 0 && (
            <>
              <p className="text-xs text-muted mt-4 mb-2">Conversations by channel</p>
              <Bars rows={s.inbox.bySource.map(r => ({ label: SOURCE_LABEL[r.source as keyof typeof SOURCE_LABEL] ?? r.source, count: r.count }))} />
            </>
          )}
        </div>

        {/* Pipeline */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Open deals by stage</h2>
            <Link to="/crm/deals/board" className="text-xs text-primary hover:underline">View board</Link>
          </div>
          <Bars rows={s.deals.byStage.map(r => ({ label: r.stageName, count: r.count, color: r.color, extra: formatMoney(r.totalValue) }))} valueLabel={n => `${n} deal${n === 1 ? '' : 's'}`} />
        </div>

        {/* Contacts by status */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title flex items-center gap-2"><Users className="w-4 h-4" /> Contacts by status</h2>
            <Link to="/crm/contacts" className="text-xs text-primary hover:underline">View all</Link>
          </div>
          <div className="space-y-2">
            {s.leads.contactsByStatus.map(({ status, count }) => (
              <div key={status} className="flex items-center justify-between py-1">
                <span style={{ fontSize: 12, fontWeight: 700, color: CONTACT_STATUS_COLOR[status] ?? 'var(--scpa-text-muted)', textTransform: 'capitalize' }}>{status}</span>
                <span className="text-sm font-semibold text-fg">{count}</span>
              </div>
            ))}
            {s.leads.contactsByStatus.length === 0 && <p className="text-sm text-muted py-4 text-center">No contacts yet.</p>}
          </div>
        </div>

        {/* Activities */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title flex items-center gap-2"><Clock className="w-4 h-4" /> Follow-ups</h2>
            <Link to="/crm/activities" className="text-xs text-primary hover:underline">View all</Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div title="Calls, meetings and tasks scheduled for later."><p className="text-2xl font-bold text-fg">{s.activities.upcoming}</p><p className="text-xs text-muted">upcoming</p></div>
            <div title="Scheduled follow-ups whose time has passed."><p className={`text-2xl font-bold ${s.activities.overdue > 0 ? 'text-err' : 'text-fg'}`}>{s.activities.overdue}</p><p className="text-xs text-muted">overdue</p></div>
          </div>
        </div>
      </div>

      {/* Quick nav */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { to: '/crm/inbox', label: 'Inbox', icon: Inbox, desc: 'Sales email and form leads' },
          { to: '/crm/contacts', label: 'Contacts', icon: Users, desc: 'People you do business with' },
          { to: '/crm/organizations', label: 'Organizations', icon: Building2, desc: 'Accounts & companies' },
          { to: '/crm/deals/board', label: 'Deals Board', icon: LayoutGrid, desc: 'Sales pipeline' },
          { to: '/crm/activities', label: 'Activities', icon: Activity, desc: 'Calls, tasks & meetings' },
        ].map(({ to, label, icon: Icon, desc }) => (
          <Link key={to} to={to} className="card hover:bg-row-odd transition-colors group">
            <Icon className="w-6 h-6 text-primary mb-2 group-hover:scale-110 transition-transform" />
            <p className="font-medium text-fg text-sm">{label}</p>
            <p className="text-xs text-muted mt-0.5">{desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
